import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useWallet } from "@/context/WalletContext";
import { createApiService } from "@/App";
import { toast } from "sonner";
import Header from "@/components/Header";
import { 
  Bitcoin, 
  Bot, 
  ArrowLeft, 
  Rocket, 
  Shield, 
  Zap, 
  TrendingUp,
  Settings,
  FileCode,
  AlertTriangle,
  ChevronRight,
  Wallet,
  Activity,
  RefreshCw,
  Gauge,
  Target,
  Eye,
  Search,
  Coins,
  Droplets
} from "lucide-react";
import SwapSTXOSHIButton from "@/components/SwapSTXOSHIButton";

const STXOSHI_LOGO = process.env.REACT_APP_LOGO_URL;

// Trading strategies for Arb Bot
const arbStrategies = [
  {
    id: "hybrid",
    name: "Hybrid (Recommended)",
    description: "Combines all strategies. Picks the strongest signal.",
    icon: <Activity className="w-5 h-5" />,
    risk: "Medium",
    riskColor: "text-yellow-400"
  },
  {
    id: "momentum",
    name: "Momentum",
    description: "Follow strong price trends. Good for trending markets.",
    icon: <TrendingUp className="w-5 h-5" />,
    risk: "Medium",
    riskColor: "text-yellow-400"
  },
  {
    id: "mean_reversion",
    name: "Mean Reversion",
    description: "Fade extreme moves. Buy dips, sell spikes.",
    icon: <RefreshCw className="w-5 h-5" />,
    risk: "Medium",
    riskColor: "text-yellow-400"
  },
  {
    id: "latency_arb",
    name: "Latency Arbitrage",
    description: "React to fast price movements. High frequency.",
    icon: <Zap className="w-5 h-5" />,
    risk: "High",
    riskColor: "text-red-400"
  }
];

// Arb Bot Strategy Component
function ArbBotStrategy({ config, setConfig, onBack, onNext }) {
  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">Select Strategy</h1>
        <p className="text-muted-foreground">Choose how your bot analyzes markets</p>
      </div>

      <Card className="glass-card max-w-xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gauge className="w-5 h-5 text-secondary" />
            Trading Strategy
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {arbStrategies.map((strategy) => (
            <button
              key={strategy.id}
              onClick={() => setConfig({ ...config, strategy: strategy.id })}
              className={`w-full p-4 rounded-xl border text-left transition-all flex items-start gap-4 ${
                config.strategy === strategy.id
                  ? 'border-secondary bg-secondary/10'
                  : 'border-border hover:border-secondary/50'
              }`}
            >
              <div className={`text-secondary ${config.strategy === strategy.id ? 'opacity-100' : 'opacity-60'}`}>
                {strategy.icon}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-medium">{strategy.name}</p>
                  <Badge variant="outline" className={strategy.riskColor}>
                    {strategy.risk} Risk
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{strategy.description}</p>
              </div>
            </button>
          ))}

          <div className="flex gap-4 mt-6">
            <Button variant="outline" onClick={onBack} className="flex-1 h-12">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button onClick={onNext} className="flex-1 bg-secondary hover:bg-secondary/90 h-12">
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Arb Bot Risk Component
function ArbBotRisk({ config, setConfig, onBack, onNext }) {
  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">Risk Management</h1>
        <p className="text-muted-foreground">Set kill switch and take profit thresholds</p>
      </div>

      <Card className="glass-card max-w-xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-secondary" />
            Risk Parameters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Kill Switch */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <Label className="text-base font-medium">Kill Switch (Stop Loss)</Label>
              <span className="font-mono text-xl text-red-400">{config.killSwitchPct}%</span>
            </div>
            <Slider
              value={[Math.abs(config.killSwitchPct)]}
              onValueChange={([v]) => setConfig({ ...config, killSwitchPct: -v })}
              min={1}
              max={50}
              step={1}
              className="w-full"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Bot stops all trading if P&L drops to {config.killSwitchPct}%
            </p>
          </div>

          {/* Take Profit */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <Label className="text-base font-medium">Take Profit</Label>
              <span className="font-mono text-xl text-green-400">+{config.takeProfitPct}%</span>
            </div>
            <Slider
              value={[config.takeProfitPct]}
              onValueChange={([v]) => setConfig({ ...config, takeProfitPct: v })}
              min={1}
              max={100}
              step={1}
              className="w-full"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Bot locks in profit when P&L reaches +{config.takeProfitPct}%
            </p>
          </div>

          {/* Preview */}
          <Card className="bg-muted/30 border-border">
            <CardContent className="p-4">
              <h4 className="font-medium mb-3">Risk/Reward Ratio</h4>
              <div className="flex justify-between text-lg">
                <span className="text-red-400">{config.killSwitchPct}%</span>
                <span className="text-primary">1:{(config.takeProfitPct / Math.abs(config.killSwitchPct)).toFixed(1)}</span>
                <span className="text-green-400">+{config.takeProfitPct}%</span>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button variant="outline" onClick={onBack} className="flex-1 h-12">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button onClick={onNext} className="flex-1 bg-secondary hover:bg-secondary/90 h-12">
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Arb Bot Deploy Component
function ArbBotDeploy({ agentName, arbConfig, apiService, navigate, addresses, isConnected, signAgentDeployment }) {
  const [loading, setLoading] = useState(false);
  const [signingTx, setSigningTx] = useState(false);
  const [walletPopupFailed, setWalletPopupFailed] = useState(false);

  const handleDeploy = async () => {
    if (!isConnected) {
      toast.error("Please connect your wallet");
      return;
    }

    setSigningTx(true);
    setWalletPopupFailed(false);
    try {
      // Generate a temporary bot ID for the memo
      const tempBotId = `bot_${Date.now().toString(36)}`;
      
      // Step 1: Sign on-chain deployment (0.0001 STX fee like regular agents)
      toast.info("Please sign the deployment transaction in your wallet...");
      
      // Set a timeout to show retry button if wallet doesn't respond
      const walletTimeout = setTimeout(() => {
        setWalletPopupFailed(true);
      }, 5000);
      
      const txResult = await signAgentDeployment(agentName, tempBotId);
      clearTimeout(walletTimeout);
      
      if (!txResult || !txResult.txid) {
        throw new Error("Transaction cancelled or failed");
      }
      
      setWalletPopupFailed(false);
      toast.success("Transaction signed! Creating bot...");
      setSigningTx(false);
      setLoading(true);

      // Step 2: Create the bot in database with txid
      const response = await apiService.post("/prediction-bots", {
        name: agentName.toUpperCase(),
        strategy: arbConfig.strategy,
        kill_switch_pct: arbConfig.killSwitchPct,
        take_profit_pct: arbConfig.takeProfitPct,
        initial_deposit_stxoshi: 0,
        deploy_txid: txResult.txid
      });

      if (response.data.success) {
        const botId = response.data.bot.id;
        console.log("[ARB BOT] Deploy success, bot ID:", botId);
        console.log("[ARB BOT] Navigating to:", `/bot/${botId}`);
        
        // Store the bot ID for redirect in case page reloads (mobile wallet return issue)
        try {
          localStorage.setItem('stxoshi_pending_bot_redirect', JSON.stringify({
            botId,
            botName: agentName,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn("[ARB BOT] Could not store redirect info:", e);
        }
        
        // Show success toast
        toast.success(`Bot ${agentName} deployed! Redirecting to dashboard...`);
        
        // Navigate immediately - use replace to prevent back button issues
        navigate(`/bot/${botId}`, { replace: true });
      } else {
        console.error("[ARB BOT] Deploy response not successful:", response.data);
        toast.error("Failed to deploy bot - unexpected response");
      }
    } catch (err) {
      console.error("Failed to deploy bot:", err);
      if (err.message?.includes("cancelled") || err.message?.includes("rejected")) {
        toast.error("Wallet transaction cancelled");
      } else {
        toast.error(err.response?.data?.detail || err.message || "Failed to deploy bot");
      }
      setWalletPopupFailed(true);
    } finally {
      setLoading(false);
      setSigningTx(false);
    }
  };
  
  // Manual retry to trigger wallet popup
  const handleRetryWalletPopup = async () => {
    setWalletPopupFailed(false);
    toast.info("Retrying wallet popup...");
    await handleDeploy();
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">Deploy Bot</h1>
        <p className="text-muted-foreground">Review and deploy your arbitrage bot</p>
      </div>

      <Card className="glass-card max-w-xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-secondary" />
            Configuration Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Summary Card */}
          <Card className="bg-muted/30 border-border">
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Bot Name</span>
                <span className="font-mono font-bold text-secondary">{agentName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Strategy</span>
                <span className="font-medium capitalize">{arbConfig.strategy.replace("_", " ")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Kill Switch</span>
                <span className="text-red-400">{arbConfig.killSwitchPct}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Take Profit</span>
                <span className="text-green-400">+{arbConfig.takeProfitPct}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Wallet</span>
                <span className="font-mono text-sm">{addresses.stx?.slice(0, 8)}...{addresses.stx?.slice(-6)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Warning */}
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-500 font-medium">Important</p>
                <p className="text-xs text-yellow-500/80 mt-1">
                  After deployment, deposit STXOSHI from your wallet to the bot's balance to start trading.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Transaction Info */}
          <div className="text-xs text-muted-foreground bg-muted/30 rounded-lg p-3 border border-border">
            <p className="font-medium text-foreground mb-1">On-Chain Registration</p>
            <p>Deploying requires a signed Stacks transaction (0.0001 STX fee).</p>
            <p className="mt-1">Your wallet will open for approval before the bot is created.</p>
          </div>

          <Button
            onClick={handleDeploy}
            disabled={loading || signingTx}
            className="w-full bg-secondary hover:bg-secondary/90 h-14 text-lg"
          >
            {signingTx ? (
              <>
                <Wallet className="w-5 h-5 mr-2 animate-pulse" />
                Sign in Wallet...
              </>
            ) : loading ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Creating Bot...
              </>
            ) : (
              <>
                <Zap className="w-5 h-5 mr-2" />
                Deploy Bot {agentName}
              </>
            )}
          </Button>
          
          {/* Blue pulsing retry button if wallet popup fails */}
          {walletPopupFailed && !loading && (
            <button
              onClick={handleRetryWalletPopup}
              className="w-full mt-3 py-3 rounded-lg font-semibold text-white 
                bg-gradient-to-r from-cyan-500 to-blue-600 
                hover:from-cyan-400 hover:to-blue-500
                animate-pulse shadow-lg shadow-cyan-500/30
                flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry Wallet Popup (if wallet didn't open)
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ========== POLLY-COPY BOT COMPONENTS ==========

// Poly-Copy Bot Strategy Component (whale tracking configuration)
function PolyCopyStrategy({ config, setConfig, onBack, onNext }) {
  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">Configure Whale Tracking</h1>
        <p className="text-muted-foreground">Set how your bot follows Polymarket whales</p>
      </div>

      <Card className="glass-card max-w-xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-400" />
            Whale Detection Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Whale Consensus Info */}
          <Card className="bg-purple-500/10 border-purple-500/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Activity className="w-5 h-5 text-purple-400 mt-0.5" />
                <div>
                  <p className="font-medium text-purple-400">AI Whale Detection</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Scans 14,000+ Polymarket whale wallets continuously. When 3+ whales 
                    enter the same position, your bot auto-copies the trade with STXOSHI.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Strategy Selection */}
          <div>
            <Label className="text-base font-medium mb-4 block">Copy Strategy</Label>
            <div className="space-y-3">
              <button
                onClick={() => setConfig({ ...config, strategy: "poly_copy" })}
                className={`w-full p-4 rounded-xl border text-left transition-all flex items-start gap-4 ${
                  config.strategy === "poly_copy"
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-border hover:border-purple-500/50'
                }`}
              >
                <Activity className={`w-5 h-5 ${config.strategy === "poly_copy" ? 'text-purple-400' : 'text-muted-foreground'}`} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">Whale Consensus</p>
                    <Badge variant="outline" className="text-purple-400 border-purple-400">
                      Recommended
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Copy trades when 3+ whales agree on a position. Most reliable signals.
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* What gets tracked */}
          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4 text-muted-foreground" />
              What We Track
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                Whale wallet positions ($50K+ volume traders)
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                Large trades ($5K+ per transaction)
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                Multi-whale consensus (when whales agree)
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                Real-time activity feed with live scanning
              </li>
            </ul>
          </div>

          <div className="flex gap-4 mt-6">
            <Button variant="outline" onClick={onBack} className="flex-1 h-12">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button onClick={onNext} className="flex-1 bg-purple-500 hover:bg-purple-600 h-12">
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Poly-Copy Bot Risk Component
function PolyCopyRisk({ config, setConfig, onBack, onNext }) {
  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">Risk Management</h1>
        <p className="text-muted-foreground">Protect your capital with automatic safeguards</p>
      </div>

      <Card className="glass-card max-w-xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-400" />
            Risk Parameters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Kill Switch */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <Label className="text-base font-medium">Kill Switch (Stop Loss)</Label>
              <span className="font-mono text-xl text-red-400">{config.killSwitchPct}%</span>
            </div>
            <Slider
              value={[Math.abs(config.killSwitchPct)]}
              onValueChange={([v]) => setConfig({ ...config, killSwitchPct: -v })}
              min={5}
              max={50}
              step={5}
              className="w-full"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Bot stops copying if total loss reaches {config.killSwitchPct}%
            </p>
          </div>

          {/* Take Profit */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <Label className="text-base font-medium">Take Profit</Label>
              <span className="font-mono text-xl text-green-400">+{config.takeProfitPct}%</span>
            </div>
            <Slider
              value={[config.takeProfitPct]}
              onValueChange={([v]) => setConfig({ ...config, takeProfitPct: v })}
              min={10}
              max={100}
              step={5}
              className="w-full"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Bot locks in profit at +{config.takeProfitPct}% gains
            </p>
          </div>

          {/* Preview */}
          <Card className="bg-muted/30 border-border">
            <CardContent className="p-4">
              <h4 className="font-medium mb-3">Risk/Reward Ratio</h4>
              <div className="flex justify-between text-lg">
                <span className="text-red-400">{config.killSwitchPct}%</span>
                <span className="text-purple-400">1:{(config.takeProfitPct / Math.abs(config.killSwitchPct)).toFixed(1)}</span>
                <span className="text-green-400">+{config.takeProfitPct}%</span>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button variant="outline" onClick={onBack} className="flex-1 h-12">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button onClick={onNext} className="flex-1 bg-purple-500 hover:bg-purple-600 h-12">
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Poly-Copy Bot Deploy Component
function PolyCopyDeploy({ agentName, polyCopyConfig, apiService, navigate, addresses, isConnected, signAgentDeployment }) {
  const [loading, setLoading] = useState(false);
  const [signingTx, setSigningTx] = useState(false);
  const [walletPopupFailed, setWalletPopupFailed] = useState(false);

  const handleDeploy = async () => {
    if (!isConnected) {
      toast.error("Please connect your wallet");
      return;
    }

    setSigningTx(true);
    setWalletPopupFailed(false);
    try {
      const tempBotId = `bot_${Date.now().toString(36)}`;
      
      toast.info("Please sign the deployment transaction in your wallet...");
      
      // Set a timeout to show retry button if wallet doesn't respond
      const walletTimeout = setTimeout(() => {
        setWalletPopupFailed(true);
      }, 5000);
      
      const txResult = await signAgentDeployment(agentName, tempBotId);
      clearTimeout(walletTimeout);
      
      if (!txResult || !txResult.txid) {
        throw new Error("Transaction cancelled or failed");
      }
      
      setWalletPopupFailed(false);
      toast.success("Transaction signed! Creating Poly-Copy bot...");
      setSigningTx(false);
      setLoading(true);

      const response = await apiService.post("/prediction-bots", {
        name: agentName.toUpperCase(),
        strategy: "poly_copy",
        bot_type: "poly_copy",
        kill_switch_pct: polyCopyConfig.killSwitchPct,
        take_profit_pct: polyCopyConfig.takeProfitPct,
        initial_deposit_stxoshi: 0,
        deploy_txid: txResult.txid
      });

      if (response.data.success) {
        const botId = response.data.bot.id;
        
        try {
          localStorage.setItem('stxoshi_pending_bot_redirect', JSON.stringify({
            botId,
            botName: agentName,
            botType: 'poly_copy',
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn("[POLLY-COPY] Could not store redirect info:", e);
        }
        
        toast.success(`Poly-Copy Bot ${agentName} deployed! Redirecting...`);
        navigate(`/bot/${botId}`, { replace: true });
      } else {
        toast.error("Failed to deploy bot - unexpected response");
      }
    } catch (err) {
      console.error("Failed to deploy Poly-Copy bot:", err);
      if (err.message?.includes("cancelled") || err.message?.includes("rejected")) {
        toast.error("Wallet transaction cancelled");
      } else {
        toast.error(err.response?.data?.detail || err.message || "Failed to deploy bot");
      }
      setWalletPopupFailed(true);
    } finally {
      setLoading(false);
      setSigningTx(false);
    }
  };
  
  // Manual retry to trigger wallet popup
  const handleRetryWalletPopup = async () => {
    setWalletPopupFailed(false);
    toast.info("Retrying wallet popup...");
    await handleDeploy();
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">Deploy Poly-Copy Bot</h1>
        <p className="text-muted-foreground">Review and deploy your whale tracking bot</p>
      </div>

      <Card className="glass-card max-w-xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-400" />
            Configuration Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Summary Card */}
          <Card className="bg-muted/30 border-border">
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Bot Name</span>
                <span className="font-mono font-bold text-purple-400">{agentName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Bot Type</span>
                <span className="font-medium flex items-center gap-2">
                  <Activity className="w-4 h-4 text-purple-400" />
                  AI Poly-Copy
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Strategy</span>
                <span className="font-medium">Whale Consensus</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Kill Switch</span>
                <span className="text-red-400">{polyCopyConfig.killSwitchPct}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Take Profit</span>
                <span className="text-green-400">+{polyCopyConfig.takeProfitPct}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Wallet</span>
                <span className="font-mono text-sm">{addresses.stx?.slice(0, 8)}...{addresses.stx?.slice(-6)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Features */}
          <Card className="bg-purple-500/10 border-purple-500/30">
            <CardContent className="p-4">
              <h4 className="font-medium text-purple-400 mb-3">What This Bot Does</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-purple-400" />
                  Scans 14,000+ Polymarket whale wallets
                </li>
                <li className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-purple-400" />
                  Detects when whales enter same positions
                </li>
                <li className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-purple-400" />
                  Auto-copies trades with your STXOSHI
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-purple-400" />
                  Real P&L settlement on-chain
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Warning */}
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-500 font-medium">Real Funds at Risk</p>
                <p className="text-xs text-yellow-500/80 mt-1">
                  This bot uses real STXOSHI from your deposited balance. Gains and losses are real.
                </p>
              </div>
            </CardContent>
          </Card>

          <Button
            onClick={handleDeploy}
            disabled={loading || signingTx}
            className="w-full bg-purple-500 hover:bg-purple-600 h-14 text-lg"
          >
            {signingTx ? (
              <>
                <Wallet className="w-5 h-5 mr-2 animate-pulse" />
                Sign in Wallet...
              </>
            ) : loading ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Creating Bot...
              </>
            ) : (
              <>
                <Activity className="w-5 h-5 mr-2" />
                Deploy Poly-Copy Bot {agentName}
              </>
            )}
          </Button>
          
          {/* Blue pulsing retry button if wallet popup fails */}
          {walletPopupFailed && !loading && (
            <button
              onClick={handleRetryWalletPopup}
              className="w-full mt-3 py-3 rounded-lg font-semibold text-white 
                bg-gradient-to-r from-cyan-500 to-blue-600 
                hover:from-cyan-400 hover:to-blue-500
                animate-pulse shadow-lg shadow-cyan-500/30
                flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry Wallet Popup (if wallet didn't open)
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// AI HYPER-COPY BOT COMPONENTS
// ============================================

// Hyper-Copy Bot Strategy Component
function HyperCopyStrategy({ config, setConfig, onBack, onNext }) {
  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">Configure Hyperliquid Tracking</h1>
        <p className="text-muted-foreground">Set how your bot follows Hyperliquid whale traders</p>
      </div>

      <Card className="glass-card max-w-xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-400" />
            Whale Detection Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Hyperliquid Info */}
          <Card className="bg-cyan-500/10 border-cyan-500/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-cyan-400 mt-0.5" />
                <div>
                  <p className="font-medium text-cyan-400">Hyperliquid Whale Tracking</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Scans top Hyperliquid traders with 55%+ win rates. When 2+ whales 
                    open similar positions, your bot auto-copies with STXOSHI.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Strategy Selection */}
          <div>
            <Label className="text-base font-medium mb-4 block">Copy Strategy</Label>
            <div className="space-y-3">
              <button
                onClick={() => setConfig({ ...config, strategy: "hyper_copy" })}
                className={`w-full p-4 rounded-xl border text-left transition-all flex items-start gap-4 ${
                  config.strategy === "hyper_copy"
                    ? 'border-cyan-500 bg-cyan-500/10'
                    : 'border-border hover:border-cyan-500/50'
                }`}
              >
                <Shield className={`w-5 h-5 ${config.strategy === "hyper_copy" ? 'text-cyan-400' : 'text-muted-foreground'}`} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">Whale Consensus</p>
                    <Badge variant="outline" className="text-cyan-400 border-cyan-400">
                      Recommended
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Copy trades when 2+ top Hyperliquid whales agree. High win-rate focused.
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* What gets tracked */}
          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4 text-muted-foreground" />
              What We Track
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                Top Hyperliquid traders ($100K+ PnL)
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                High win-rate traders (55%+ success)
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                Large positions ($5K+ per trade)
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                BTC, ETH, HYPE, SOL, ARB movements
              </li>
            </ul>
          </div>

          <div className="flex gap-4 mt-6">
            <Button variant="outline" onClick={onBack} className="flex-1 h-12">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button onClick={onNext} className="flex-1 bg-cyan-500 hover:bg-cyan-600 h-12">
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Hyper-Copy Bot Risk Component
function HyperCopyRisk({ config, setConfig, onBack, onNext }) {
  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">Risk Management</h1>
        <p className="text-muted-foreground">Protect your capital with automatic safeguards</p>
      </div>

      <Card className="glass-card max-w-xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-400" />
            Risk Parameters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Kill Switch */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <Label className="text-base font-medium">Kill Switch (Stop Loss)</Label>
              <span className="font-mono text-xl text-red-400">{config.killSwitchPct}%</span>
            </div>
            <Slider
              value={[Math.abs(config.killSwitchPct)]}
              onValueChange={([v]) => setConfig({ ...config, killSwitchPct: -v })}
              min={5}
              max={50}
              step={5}
              className="w-full"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Bot stops copying if total loss reaches {config.killSwitchPct}%
            </p>
          </div>

          {/* Take Profit */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <Label className="text-base font-medium">Take Profit</Label>
              <span className="font-mono text-xl text-green-400">+{config.takeProfitPct}%</span>
            </div>
            <Slider
              value={[config.takeProfitPct]}
              onValueChange={([v]) => setConfig({ ...config, takeProfitPct: v })}
              min={10}
              max={100}
              step={5}
              className="w-full"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Bot locks in profit at +{config.takeProfitPct}% gains
            </p>
          </div>

          {/* Preview */}
          <Card className="bg-muted/30 border-border">
            <CardContent className="p-4">
              <h4 className="font-medium mb-3">Risk/Reward Ratio</h4>
              <div className="flex justify-between text-lg">
                <span className="text-red-400">{config.killSwitchPct}%</span>
                <span className="text-cyan-400">1:{(config.takeProfitPct / Math.abs(config.killSwitchPct)).toFixed(1)}</span>
                <span className="text-green-400">+{config.takeProfitPct}%</span>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button variant="outline" onClick={onBack} className="flex-1 h-12">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button onClick={onNext} className="flex-1 bg-cyan-500 hover:bg-cyan-600 h-12">
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Hyper-Copy Bot Deploy Component
function HyperCopyDeploy({ agentName, hyperCopyConfig, apiService, navigate, addresses, isConnected, signAgentDeployment }) {
  const [loading, setLoading] = useState(false);
  const [signingTx, setSigningTx] = useState(false);
  const [walletPopupFailed, setWalletPopupFailed] = useState(false);

  const handleDeploy = async () => {
    if (!isConnected) {
      toast.error("Please connect your wallet");
      return;
    }

    setSigningTx(true);
    setWalletPopupFailed(false);
    try {
      const tempBotId = `bot_${Date.now().toString(36)}`;
      
      toast.info("Please sign the deployment transaction in your wallet...");
      
      // Set a timeout to show retry button if wallet doesn't respond
      const walletTimeout = setTimeout(() => {
        setWalletPopupFailed(true);
      }, 5000);
      
      const txResult = await signAgentDeployment(agentName, tempBotId);
      clearTimeout(walletTimeout);
      
      if (!txResult || !txResult.txid) {
        throw new Error("Transaction cancelled or failed");
      }
      
      setWalletPopupFailed(false);
      toast.success("Transaction signed! Creating Hyper-Copy bot...");
      setSigningTx(false);
      setLoading(true);

      const response = await apiService.post("/prediction-bots", {
        name: agentName.toUpperCase(),
        strategy: "hyper_copy",
        bot_type: "hyper_copy",
        kill_switch_pct: hyperCopyConfig.killSwitchPct,
        take_profit_pct: hyperCopyConfig.takeProfitPct,
        initial_deposit_stxoshi: 0,
        deploy_txid: txResult.txid
      });

      if (response.data.success) {
        const botId = response.data.bot.id;
        
        try {
          localStorage.setItem('stxoshi_pending_bot_redirect', JSON.stringify({
            botId,
            botName: agentName,
            botType: 'hyper_copy',
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn("[HYPER-COPY] Could not store redirect info:", e);
        }
        
        toast.success(`Hyper-Copy Bot ${agentName} deployed! Redirecting...`);
        navigate(`/bot/${botId}`, { replace: true });
      } else {
        toast.error("Failed to deploy bot - unexpected response");
      }
    } catch (err) {
      console.error("Failed to deploy Hyper-Copy bot:", err);
      if (err.message?.includes("cancelled") || err.message?.includes("rejected")) {
        toast.error("Wallet transaction cancelled");
      } else {
        toast.error(err.response?.data?.detail || err.message || "Failed to deploy bot");
      }
      setWalletPopupFailed(true);
    } finally {
      setLoading(false);
      setSigningTx(false);
    }
  };
  
  // Manual retry to trigger wallet popup
  const handleRetryWalletPopup = async () => {
    setWalletPopupFailed(false);
    toast.info("Retrying wallet popup...");
    await handleDeploy();
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">Deploy Your Hyper-Copy Bot</h1>
        <p className="text-muted-foreground">Review settings and launch your Hyperliquid whale tracker</p>
      </div>

      <Card className="glass-card max-w-xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-400" />
            Bot Summary: {agentName}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Config Summary */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground mb-1">Stop Loss</p>
                <span className="text-2xl font-bold text-red-400">{hyperCopyConfig.killSwitchPct}%</span>
              </CardContent>
            </Card>
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground mb-1">Take Profit</p>
                <span className="text-2xl font-bold text-green-400">+{hyperCopyConfig.takeProfitPct}%</span>
              </CardContent>
            </Card>
          </div>

          {/* Features */}
          <Card className="bg-muted/30 border-border">
            <CardContent className="p-4">
              <h4 className="font-medium mb-3 text-cyan-400">What Your Bot Does</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-cyan-400" />
                  Scans Hyperliquid top traders 24/7
                </li>
                <li className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-cyan-400" />
                  Tracks 55%+ win-rate whales
                </li>
                <li className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-cyan-400" />
                  Auto-copies trades with your STXOSHI
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-cyan-400" />
                  Real P&L settlement on-chain
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Warning */}
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-500 font-medium">Real Funds at Risk</p>
                <p className="text-xs text-yellow-500/80 mt-1">
                  This bot uses real STXOSHI from your deposited balance. Gains and losses are real.
                </p>
              </div>
            </CardContent>
          </Card>

          <Button
            onClick={handleDeploy}
            disabled={loading || signingTx}
            className="w-full bg-cyan-500 hover:bg-cyan-600 h-14 text-lg"
          >
            {signingTx ? (
              <>
                <Wallet className="w-5 h-5 mr-2 animate-pulse" />
                Sign in Wallet...
              </>
            ) : loading ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Creating Bot...
              </>
            ) : (
              <>
                <Shield className="w-5 h-5 mr-2" />
                Deploy Hyper-Copy Bot {agentName}
              </>
            )}
          </Button>
          
          {/* Blue pulsing retry button if wallet popup fails */}
          {walletPopupFailed && !loading && (
            <button
              onClick={handleRetryWalletPopup}
              className="w-full mt-3 py-3 rounded-lg font-semibold text-white 
                bg-gradient-to-r from-cyan-500 to-blue-600 
                hover:from-cyan-400 hover:to-blue-500
                animate-pulse shadow-lg shadow-cyan-500/30
                flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry Wallet Popup (if wallet didn't open)
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AgentCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isConnected, user, connectWallet, getAuthHeaders, addresses, signAgentDeployment } = useWallet();
  const apiService = createApiService(getAuthHeaders);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [signingTx, setSigningTx] = useState(false);
  
  // Check if coming from Bot creation (pre-select Arbitrage archetype)
  const preSelectBot = searchParams.get('type') === 'bot';
  
  // Arb Bot specific config
  const [arbBotConfig, setArbBotConfig] = useState({
    strategy: "hybrid",
    killSwitchPct: -5,
    takeProfitPct: 10,
    initialDeposit: ""
  });
  
  // Poly-Copy Bot specific config
  const [polyCopyConfig, setPolyCopyConfig] = useState({
    strategy: "poly_copy",
    killSwitchPct: -20,
    takeProfitPct: 30,
    minWhaleConsensus: 3
  });
  
  // Hyper-Copy Bot specific config
  const [hyperCopyConfig, setHyperCopyConfig] = useState({
    strategy: "hyper_copy",
    killSwitchPct: -15,
    takeProfitPct: 25,
    minWhaleConsensus: 2
  });
  
  const [config, setConfig] = useState({
    name: "",
    archetype: preSelectBot ? "Arbitrage Prediction Bot Hunter" : "",
    priority_1: "Capital Preservation",
    priority_2: "Yield Maximization",
    priority_3: "Fee Generation",
    max_slippage: 0.8,
    min_stx_reserve: 20,
    peg_deviation_threshold: 1.5,
    risk_tier_1: ["Reading market prices", "Checking peg status", "Collecting rewards"],
    risk_tier_2: ["Rebalancing between protocols", "APY optimization"],
    risk_tier_3: ["Large transactions > 0.1 sBTC", "High slippage swaps"]
  });
  
  // Option to skip on-chain registration (for users who have wallet issues)
  const [registerOnChain, setRegisterOnChain] = useState(true);

  const archetypes = [
    { 
      name: "AI Hyper-Copy Bot", 
      description: "Scan Whale Wallet Trades",
      icon: <Shield className="w-5 h-5" />,
      color: "text-cyan-400",
      badge: "Whale Hunter",
      badgeColor: "bg-cyan-500/20 text-cyan-400 border-cyan-500/50"
    },
    { 
      name: "Yield Maximizer", 
      description: "STXOSHI Vault, Zest, Bitflow, Alex, Hermetica, StackingDAO, Velar",
      icon: <TrendingUp className="w-5 h-5" />,
      color: "text-primary",
      badge: "Protocol Yield",
      badgeColor: "bg-orange-500/20 text-orange-400 border-orange-500/50"
    },
    { 
      name: "Arbitrage Prediction Bot Hunter", 
      description: "Fast execution, spread capture focused",
      icon: <Zap className="w-5 h-5" />,
      color: "text-secondary",
      badge: "Quant Bot",
      badgeColor: "bg-red-500/20 text-red-400 border-red-500/50"
    },
    { 
      name: "AI Poly-Copy Bot", 
      description: "Scan Whale Wallet Trades",
      icon: <Activity className="w-5 h-5" />,
      color: "text-purple-400",
      badge: "Whale Hunter",
      badgeColor: "bg-purple-500/20 text-purple-400 border-purple-500/50"
    }
  ];

  const handleCreate = async () => {
    console.log('[AGENT CREATE] handleCreate called');
    console.log('[AGENT CREATE] isConnected:', isConnected);
    console.log('[AGENT CREATE] registerOnChain:', registerOnChain);
    
    if (!isConnected) {
      toast.error("Please connect your wallet first");
      connectWallet();
      return;
    }
    
    if (!config.name.trim()) {
      toast.error("Please enter an agent name");
      return;
    }
    
    if (config.name.trim().length > 2) {
      toast.error("Agent name must be 2 characters or less");
      return;
    }
    
    // Generate a temporary agent ID for the on-chain registration
    const tempAgentId = `agent_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    console.log('[AGENT CREATE] Generated agent ID:', tempAgentId);
    
    setLoading(true);
    
    let txid = null;
    let txMemo = null;
    
    // Step 1: If user wants on-chain registration, attempt to sign transaction
    // CRITICAL: This must happen SYNCHRONOUSLY within this click handler
    if (registerOnChain) {
      setSigningTx(true);
      
      try {
        console.log('[AGENT CREATE] Starting on-chain registration...');
        toast.info("Opening wallet for signature...", {
          description: "Please approve the transaction in your Xverse wallet",
          duration: 15000
        });
        
        // This MUST be called directly from click handler for iOS
        const txResult = await signAgentDeployment(config.name, tempAgentId);
        console.log('[AGENT CREATE] Transaction result:', txResult);
        
        if (txResult.success) {
          txid = txResult.txid;
          txMemo = txResult.memo;
          toast.success("Transaction signed!", {
            description: `TX: ${txid.slice(0, 16)}...`,
            duration: 5000
          });
        }
      } catch (err) {
        console.error("[AGENT CREATE] Transaction signing failed:", err);
        console.error("[AGENT CREATE] Error message:", err.message);
        console.error("[AGENT CREATE] Full error:", JSON.stringify(err, Object.getOwnPropertyNames(err)));
        
        // If transaction fails, ask user if they want to continue without on-chain registration
        const continueWithoutTx = window.confirm(
          `Transaction signing failed: ${err.message || 'Unknown error'}\n\n` +
          "Would you like to create the agent without on-chain registration?\n\n" +
          "You can register it on-chain later from the agent dashboard."
        );
        
        console.log("[AGENT CREATE] User chose to continue without TX:", continueWithoutTx);
        
        if (!continueWithoutTx) {
          console.log("[AGENT CREATE] User cancelled, aborting agent creation");
          setLoading(false);
          setSigningTx(false);
          return;
        }
        
        console.log("[AGENT CREATE] Proceeding with offline agent creation (no txid)");
        toast.info("Creating agent without on-chain registration");
      }
      
      setSigningTx(false);
    }
    
    // Step 2: Create agent in database
    try {
      console.log("[AGENT CREATE] Creating agent in database...");
      
      const agentPayload = {
        name: config.name,
        config: config,
        btc_address: addresses.btc || null,
        ordinals_address: addresses.ordinals || null,
        use_real_wallet: true,
        registration_txid: txid || null,
        registration_memo: txMemo || null
      };
      
      console.log("[AGENT CREATE] Payload:", JSON.stringify(agentPayload, null, 2));
      console.log("[AGENT CREATE] API endpoint:", apiService);
      
      toast.info(txid ? "Creating agent record..." : "Creating agent...", {
        description: txid ? `TX: ${txid.slice(0, 16)}...` : "Storing your agent configuration",
        duration: 5000
      });
      
      const response = await apiService.createAgent(agentPayload);
      
      console.log("[AGENT CREATE] Agent created successfully:", response.data);
      
      if (txid) {
        toast.success("Agent deployed on-chain!", {
          description: `Transaction: ${txid.slice(0, 20)}...`,
          duration: 8000
        });
      } else {
        toast.success("Agent created successfully!", {
          description: "You can register it on-chain later",
          duration: 5000
        });
      }
      
      navigate(`/agent/${response.data.id}`);
      
    } catch (err) {
      console.error("[AGENT CREATE] Failed to create agent:", err);
      console.error("[AGENT CREATE] Error details:", err.response?.data || err.message);
      
      // Better error message based on error type
      let errorMsg = "Failed to create agent";
      let errorDesc = "";
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMsg = "Request timed out";
        errorDesc = "The server is busy. Your on-chain transaction succeeded - please refresh and check your agents.";
      } else if (err.message === 'Network Error') {
        errorMsg = "Network Error";
        errorDesc = "Connection failed. Your on-chain transaction may have succeeded - please refresh and check your agents.";
      } else {
        errorDesc = err.response?.data?.detail || err.message || "Unknown error";
      }
      
      toast.error(errorMsg, {
        description: errorDesc,
        duration: 10000
      });
    } finally {
      setLoading(false);
      setSigningTx(false);
    }
  };

  const soulMdPreview = `# SOUL.md - ${config.name || 'Treasury Agent'}

## Personality & Tone
- **Archetype:** ${config.archetype}
- **Traits:** Stoic, data-driven, and brief
- **Core Belief:** Bitcoin is the apex collateral

## Operational Guardrails
1. **Risk Tier 1 (Auto-Execute):**
${config.risk_tier_1.map(r => `   - ${r}`).join('\n')}

2. **Risk Tier 2 (Notify & Wait 5m):**
${config.risk_tier_2.map(r => `   - ${r}`).join('\n')}

3. **Risk Tier 3 (Manual Approval):**
${config.risk_tier_3.map(r => `   - ${r}`).join('\n')}

## Strategic Priority
- Priority 1: ${config.priority_1}
- Priority 2: ${config.priority_2}
- Priority 3: ${config.priority_3}

## Risk Parameters
- Max Slippage: ${config.max_slippage}%
- Min STX Reserve: ${config.min_stx_reserve} STX
- Peg Deviation Alert: ${config.peg_deviation_threshold}%`;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Wallet Connection Required */}
          {!isConnected && (
            <Card className="glass-card border-primary/30 mb-8">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold mb-1">Wallet Required</h3>
                    <p className="text-sm text-muted-foreground">
                      Connect your Xverse or Leather wallet to create an agent
                    </p>
                  </div>
                  <Button 
                    onClick={connectWallet}
                    className="bg-primary text-primary-foreground"
                  >
                    <Wallet className="w-4 h-4 mr-2" />
                    Connect Wallet
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-12">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <button
                  onClick={() => setStep(s)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-mono text-sm transition-all ${
                    step === s 
                      ? 'bg-primary text-primary-foreground shadow-neon-orange' 
                      : step > s 
                        ? 'bg-primary/20 text-primary' 
                        : 'bg-muted text-muted-foreground'
                  }`}
                  data-testid={`step-${s}`}
                >
                  {s}
                </button>
                {s < 3 && (
                  <div className={`w-20 h-0.5 mx-2 ${step > s ? 'bg-primary' : 'bg-border'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Identity */}
          {step === 1 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <h1 className="font-heading text-4xl font-bold mb-2">Name Your Bots & Agents</h1>
                <p className="text-muted-foreground">Give your Treasury Agent a unique identity</p>
              </div>

              <Card className="glass-card max-w-lg mx-auto">
                <CardContent className="p-8">
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium mb-2 block">Agent Name</Label>
                      <Input
                        id="name"
                        placeholder="Max 2 Characters, e.g., 'IN', 'XS'"
                        value={config.name}
                        onChange={(e) => setConfig({ ...config, name: e.target.value })}
                        maxLength={2}
                        className="bg-muted/50 border-border focus:border-primary text-lg h-14"
                        data-testid="agent-name-input"
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-medium mb-4 block">Select Archetype</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {archetypes.map((arch) => (
                          <button
                            key={arch.name}
                            onClick={() => {
                              // Just select the archetype - bot creation happens in step 2
                              setConfig({ ...config, archetype: arch.name });
                            }}
                            className={`p-4 rounded-xl border text-left transition-all ${
                              config.archetype === arch.name 
                                ? 'border-primary bg-primary/10' 
                                : 'border-border hover:border-primary/50'
                            } ${arch.name === "Arbitrage Prediction Bot Hunter" && config.archetype === arch.name ? 'border-secondary bg-secondary/10' : ''}
                            ${arch.name === "AI Poly-Copy Bot" && config.archetype === arch.name ? 'border-purple-500 bg-purple-500/10' : ''}
                            ${arch.name === "AI Hyper-Copy Bot" && config.archetype === arch.name ? 'border-cyan-500 bg-cyan-500/10' : ''}
                            ${arch.name === "Yield Maximizer" && config.archetype === arch.name ? 'border-orange-500 bg-orange-500/10' : ''}`}
                            data-testid={`archetype-${arch.name.toLowerCase().replace(/\s/g, '-')}`}
                          >
                            <div className={`${arch.color} mb-2`}>{arch.icon}</div>
                            <p className="font-medium text-sm">{arch.name}</p>
                            
                            {/* Special STXOSHI Vault badge for Yield Maximizer */}
                            {arch.name === "Yield Maximizer" && (
                              <div className="flex items-center gap-1.5 mt-1.5 mb-1 flex-wrap">
                                <span className="text-sm">🔥</span>
                                <span className="text-amber-400 font-semibold text-xs">STXOSHI Vault</span>
                                <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                                  High Yield 25%
                                </span>
                              </div>
                            )}
                            
                            <p className="text-xs text-muted-foreground">
                              {arch.name === "Yield Maximizer" 
                                ? "Zest, Bitflow, Alex, Hermetica, StackingDAO, Velar" 
                                : arch.description}
                            </p>
                            {arch.badge && (
                              <span className={`inline-block mt-2 px-2 py-0.5 text-xs rounded-full border ${arch.badgeColor}`}>
                                {arch.badge}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    <Button 
                      onClick={() => setStep(2)}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12"
                      disabled={!config.name.trim() || !config.archetype}
                      data-testid="next-step-1"
                    >
                      Continue
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 2: Strategy - Different for Arbitrage Bot, Poly-Copy Bot, Hyper-Copy Bot, vs Regular Agents */}
          {step === 2 && config.archetype === "Arbitrage Prediction Bot Hunter" ? (
            <ArbBotStrategy 
              config={arbBotConfig}
              setConfig={setArbBotConfig}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
            />
          ) : step === 2 && config.archetype === "AI Poly-Copy Bot" ? (
            <PolyCopyStrategy
              config={polyCopyConfig}
              setConfig={setPolyCopyConfig}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
            />
          ) : step === 2 && config.archetype === "AI Hyper-Copy Bot" ? (
            <HyperCopyStrategy
              config={hyperCopyConfig}
              setConfig={setHyperCopyConfig}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
            />
          ) : step === 2 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <h1 className="font-heading text-4xl font-bold mb-2">Configure Strategy</h1>
                <p className="text-muted-foreground">Set your agent's priorities and risk parameters</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      Strategic Priorities
                    </CardTitle>
                    <CardDescription>Order determines agent's decision hierarchy</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Priority 1 (Highest)</Label>
                      <Input
                        value={config.priority_1}
                        onChange={(e) => setConfig({ ...config, priority_1: e.target.value })}
                        className="bg-muted/50 border-border mt-1"
                        data-testid="priority-1-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Priority 2</Label>
                      <Input
                        value={config.priority_2}
                        onChange={(e) => setConfig({ ...config, priority_2: e.target.value })}
                        className="bg-muted/50 border-border mt-1"
                        data-testid="priority-2-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Priority 3</Label>
                      <Input
                        value={config.priority_3}
                        onChange={(e) => setConfig({ ...config, priority_3: e.target.value })}
                        className="bg-muted/50 border-border mt-1"
                        data-testid="priority-3-input"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      Risk Parameters
                    </CardTitle>
                    <CardDescription>Safety thresholds and limits</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs text-muted-foreground">Max Slippage</Label>
                        <span className="font-mono text-sm text-primary">{config.max_slippage}%</span>
                      </div>
                      <Slider
                        value={[config.max_slippage]}
                        onValueChange={([value]) => setConfig({ ...config, max_slippage: value })}
                        max={2}
                        step={0.1}
                        className="w-full"
                        data-testid="slippage-slider"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs text-muted-foreground">Min STX Reserve</Label>
                        <span className="font-mono text-sm text-primary">{config.min_stx_reserve} STX</span>
                      </div>
                      <Slider
                        value={[config.min_stx_reserve]}
                        onValueChange={([value]) => setConfig({ ...config, min_stx_reserve: value })}
                        max={100}
                        step={5}
                        className="w-full"
                        data-testid="stx-reserve-slider"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs text-muted-foreground">Peg Deviation Alert</Label>
                        <span className="font-mono text-sm text-primary">{config.peg_deviation_threshold}%</span>
                      </div>
                      <Slider
                        value={[config.peg_deviation_threshold]}
                        onValueChange={([value]) => setConfig({ ...config, peg_deviation_threshold: value })}
                        max={5}
                        step={0.5}
                        className="w-full"
                        data-testid="peg-deviation-slider"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex gap-4 mt-8 mb-24">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="flex-1 h-12"
                  data-testid="back-step-2"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 h-12"
                  data-testid="next-step-2"
                >
                  Review SOUL.md
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Risk (for Arb Bot), Risk (for Poly-Copy Bot), Risk (for Hyper-Copy Bot), OR Review & Deploy (for regular agents) */}
          {step === 3 && config.archetype === "Arbitrage Prediction Bot Hunter" ? (
            <ArbBotRisk 
              config={arbBotConfig}
              setConfig={setArbBotConfig}
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
            />
          ) : step === 3 && config.archetype === "AI Poly-Copy Bot" ? (
            <PolyCopyRisk
              config={polyCopyConfig}
              setConfig={setPolyCopyConfig}
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
            />
          ) : step === 3 && config.archetype === "AI Hyper-Copy Bot" ? (
            <HyperCopyRisk
              config={hyperCopyConfig}
              setConfig={setHyperCopyConfig}
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
            />
          ) : step === 3 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <h1 className="font-heading text-4xl font-bold mb-2">Review & Deploy</h1>
                <p className="text-muted-foreground">Your agent's SOUL.md configuration</p>
              </div>

              <Card className="glass-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileCode className="w-5 h-5 text-primary" />
                      SOUL.md Preview
                    </CardTitle>
                    <Badge variant="outline" className="font-mono text-xs">
                      {config.archetype}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted/50 rounded-lg p-6 font-mono text-sm overflow-auto max-h-[400px] whitespace-pre-wrap">
                    {soulMdPreview}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card mt-6 border-primary/30">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Rocket className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold mb-1">Ready to Deploy</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Your agent will be linked to your <span className="text-primary font-medium">real wallet</span> and fetch live balances from Stacks mainnet.
                      </p>
                      
                      {/* Connected Wallet Info */}
                      <div className="bg-muted/30 rounded-lg p-4 mb-4 border border-border">
                        <p className="text-xs text-muted-foreground mb-2">Connected Wallet (Mainnet)</p>
                        <p className="font-mono text-sm text-foreground truncate">
                          {addresses.stx || 'Not connected'}
                        </p>
                        {addresses.btc && (
                          <p className="font-mono text-xs text-muted-foreground mt-1 truncate">
                            BTC: {addresses.btc}
                          </p>
                        )}
                      </div>
                      
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                          Real sBTC & STX balances from your wallet
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                          Transactions require your wallet approval
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                          AI decision engine powered by Gemini 3 Flash
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                          <span className="text-orange-400">Mainnet - Real funds at risk</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* On-Chain Registration Option */}
              <Card className="glass-card mt-6 border-border">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <input
                      type="checkbox"
                      id="registerOnChain"
                      checked={registerOnChain}
                      onChange={(e) => setRegisterOnChain(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded border-border bg-muted accent-primary"
                      data-testid="register-onchain-checkbox"
                    />
                    <div className="flex-1">
                      <label htmlFor="registerOnChain" className="font-heading font-semibold cursor-pointer">
                        Register agent on-chain
                      </label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {registerOnChain ? (
                          <>Creates a permanent record on Stacks mainnet. Requires <span className="text-primary font-medium">0.001 STX</span> fee and wallet signature.</>
                        ) : (
                          <>Agent will be created without blockchain registration. You can register later from the dashboard.</>
                        )}
                      </p>
                      {!registerOnChain && (
                        <div className="mt-2 bg-yellow-500/10 text-yellow-400 text-xs p-2 rounded border border-yellow-500/30">
                          Note: Skipping on-chain registration means your agent won't be publicly verifiable on the blockchain.
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-4 mt-8 mb-24">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(2)}
                  className="flex-1 h-12"
                  data-testid="back-step-3"
                  disabled={loading}
                >
                  Back
                </Button>
                <Button 
                  onClick={handleCreate}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 shadow-neon-orange h-12"
                  disabled={loading}
                  data-testid="deploy-agent-btn"
                >
                  {signingTx ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mr-2" />
                      Waiting for Signature...
                    </>
                  ) : loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mr-2" />
                      Creating Agent...
                    </>
                  ) : registerOnChain ? (
                    <>
                      <Rocket className="w-4 h-4 mr-2" />
                      Sign & Deploy Agent
                    </>
                  ) : (
                    <>
                      <Rocket className="w-4 h-4 mr-2" />
                      Create Agent
                    </>
                  )}
                </Button>
              </div>
              
              {/* Transaction Info */}
              {registerOnChain && (
                <div className="text-xs text-muted-foreground bg-muted/30 rounded-lg p-3 border border-border">
                  <p className="font-medium text-foreground mb-1">On-Chain Registration</p>
                  <p>Deploying requires a signed Stacks transaction (0.001 STX fee).</p>
                  <p className="mt-1">Your wallet will open for approval before the agent is created.</p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Deploy Arb Bot, Poly-Copy Bot, or Hyper-Copy Bot */}
          {step === 4 && config.archetype === "Arbitrage Prediction Bot Hunter" && (
            <ArbBotDeploy 
              agentName={config.name}
              arbConfig={arbBotConfig}
              apiService={apiService}
              navigate={navigate}
              addresses={addresses}
              isConnected={isConnected}
              signAgentDeployment={signAgentDeployment}
            />
          )}
          
          {step === 4 && config.archetype === "AI Poly-Copy Bot" && (
            <PolyCopyDeploy
              agentName={config.name}
              polyCopyConfig={polyCopyConfig}
              apiService={apiService}
              navigate={navigate}
              addresses={addresses}
              isConnected={isConnected}
              signAgentDeployment={signAgentDeployment}
            />
          )}
          
          {step === 4 && config.archetype === "AI Hyper-Copy Bot" && (
            <HyperCopyDeploy
              agentName={config.name}
              hyperCopyConfig={hyperCopyConfig}
              apiService={apiService}
              navigate={navigate}
              addresses={addresses}
              isConnected={isConnected}
              signAgentDeployment={signAgentDeployment}
            />
          )}
        </div>
      </main>
      {/* Buy STXOSHI Floating Button */}
      <div className="fixed bottom-24 right-6 z-40">
        <SwapSTXOSHIButton className="shadow-lg" />
      </div>
      
      {/* Bottom spacer for "Made with Emergent" badge */}
      <div className="h-20" />
    </div>
  );
}
